/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TelephonyDomainsActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for telephony domains data grid.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { Domain } from '../types';

interface TelephonyDomainsActionsProps {
  domain: Domain;
  onEdit: (domain: Domain) => void;
  onDuplicate: (domain: Domain) => void;
  onDelete: (domain: Domain) => void;
}

export function TelephonyDomainsActions({
  domain,
  onEdit,
  onDuplicate,
  onDelete
}: TelephonyDomainsActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('telephonyDomains.tooltips.editDomain')} position="top">
        <button
          onClick={() => onEdit(domain)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          type="button"
          aria-label={t('telephonyDomains.tooltips.editDomain')}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('telephonyDomains.tooltips.duplicateDomain')} position="top">
        <button
          onClick={() => onDuplicate(domain)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          type="button"
          aria-label={t('telephonyDomains.tooltips.duplicateDomain')}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip
        content={domain.isPrimary ? t('telephonyDomains.tooltips.cannotDeletePrimary') : t('telephonyDomains.tooltips.deleteDomain')}
        position="top"
      >
        <button
          onClick={() => onDelete(domain)}
          disabled={domain.isPrimary}
          className={`p-2 transition-colors ${domain.isPrimary
              ? 'text-gray-400 cursor-not-allowed dark:text-gray-600'
              : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
            }`}
          type="button"
          aria-label={t('telephonyDomains.tooltips.deleteDomain')}
        >
          <Trash2 className={`h-4 w-4 ${domain.isPrimary ? 'opacity-50' : ''}`} />
        </button>
      </SimpleTooltip>
    </div>
  );
}

